import React, { useEffect, useMemo, useState } from "react";

const MODE_COPY = {
  cross:
    "All cross positions under {pair} share the same margin balance. If liquidated, your full margin balance and any remaining open positions under this asset may be forfeited.",
  isolated:
    "Risk is managed per position by capping the margin allocated to it. If a position's margin ratio reaches 100%, only that position is liquidated. You can add or remove margin on isolated positions anytime.",
};

/**
 * Futures margin mode picker — matches redesign (segmented Cross/Isolated).
 */
export default function FuturesMarginModeModal({
  open,
  onClose,
  onConfirm,
  onOpenDefaults,
  currentMode = "cross",
  pairLabel = "BTC/USDT",
  locked = false,
  batchAdjustActive = false,
  batchAdjustTrail = "Off",
}) {
  const [draft, setDraft] = useState(currentMode === "isolated" ? "isolated" : "cross");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(currentMode === "isolated" ? "isolated" : "cross");
      setSubmitting(false);
    }
  }, [open, currentMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const desc = useMemo(() => {
    const tpl = MODE_COPY[draft] || MODE_COPY.cross;
    return tpl.replace(/\{pair\}/g, pairLabel || "BTC/USDT");
  }, [draft, pairLabel]);

  const canConfirm = !locked && draft !== currentMode;

  if (!open) return null;

  return (
    <>
      <div
        className="modal-backdrop fade show agce-ft-mm-backdrop"
        onClick={onClose}
        aria-hidden
      />
      <div
        className="modal fade kyc_modal margin-modal-container show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agce-ft-mm-title"
        style={{ zIndex: 1055 }}
      >
        <div className="modal-dialog modal-dialog-centered agce-ft-mm-dialog" role="document">
          <div className="modal-content agce-ft-mm-content">
            <div className="agce-ft-mm-body">
              <div className="agce-ft-mm-header">
                <div>
                  <h2 id="agce-ft-mm-title" className="agce-ft-mm-title">
                    Margin mode
                  </h2>
                  <p className="agce-ft-mm-subtitle">
                    {pairLabel} perpetual · applies to this contract only
                  </p>
                </div>
                <button
                  type="button"
                  className="agce-ft-mm-close"
                  aria-label="Close"
                  onClick={onClose}
                >
                  ×
                </button>
              </div>

              <div
                className="agce-ft-mm-segment"
                role="tablist"
                aria-label="Margin mode"
              >
                <span
                  className={`agce-ft-mm-segment__thumb${
                    draft === "isolated" ? " is-isolated" : ""
                  }`}
                  aria-hidden
                />
                <button
                  type="button"
                  role="tab"
                  aria-selected={draft === "cross"}
                  className={`agce-ft-mm-segment__btn${
                    draft === "cross" ? " is-active" : ""
                  }`}
                  onClick={() => setDraft("cross")}
                >
                  Cross
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={draft === "isolated"}
                  className={`agce-ft-mm-segment__btn${
                    draft === "isolated" ? " is-active" : ""
                  }`}
                  onClick={() => setDraft("isolated")}
                >
                  Isolated
                </button>
              </div>

              <div className="agce-ft-mm-desc">
                <p>{desc}</p>
              </div>

              {locked ? (
                <div className="agce-ft-mm-warn" role="status">
                  <span className="agce-ft-mm-warn__icon" aria-hidden>
                    ⚠
                  </span>
                  <p>Mode can&apos;t change while you have an open order or position.</p>
                </div>
              ) : null}

              <button
                type="button"
                className="agce-ft-mm-defaults"
                onClick={() => onOpenDefaults?.()}
              >
                <span>Default leverage &amp; margin mode</span>
                <span className="agce-ft-mm-defaults__trail">
                  {batchAdjustTrail || (batchAdjustActive ? "On" : "Off")}{" "}
                  <span aria-hidden>›</span>
                </span>
              </button>

              <button
                type="button"
                className="agce-ft-mm-confirm"
                disabled={!canConfirm || submitting}
                onClick={async () => {
                  if (!canConfirm || submitting) return;
                  setSubmitting(true);
                  try {
                    const ok = await onConfirm?.(draft);
                    if (ok !== false) onClose?.();
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {submitting ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
